import * as THREE from 'three';
import type { Random } from '../core/Random';
import type { CollisionWorld } from './Collision';
import { LITTER_AREAS, type Rect } from './layout';
import { cyl, M, mat, mesh } from './materials';
import { glowTexture } from './textures';

export type PickupKind = 'can' | 'scrap';

interface Pickup {
  kind: PickupKind;
  object: THREE.Group;
  glint: THREE.Sprite;
  x: number;
  z: number;
  phase: number;
}

const MAX_PICKUPS = 40;

/** Litter you can grab by walking over it: empty cans and scrap metal. */
export class Pickups {
  private readonly items: Pickup[] = [];
  private readonly group = new THREE.Group();
  private readonly glintMat = new THREE.SpriteMaterial({
    map: glowTexture(),
    color: '#fff6c0',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  constructor(
    parent: THREE.Object3D,
    private readonly collision: CollisionWorld,
    private readonly rng: Random,
  ) {
    parent.add(this.group);
  }

  get count(): number {
    return this.items.length;
  }

  clear(): void {
    for (const p of this.items) p.object.removeFromParent();
    this.items.length = 0;
  }

  spawn(kind: PickupKind, n: number, areas: readonly Rect[] = LITTER_AREAS): void {
    for (let i = 0; i < n && this.items.length < MAX_PICKUPS; i++) {
      for (let attempt = 0; attempt < 12; attempt++) {
        const area = this.rng.pick(areas);
        const x = this.rng.range(area.minX, area.maxX);
        const z = this.rng.range(area.minZ, area.maxZ);
        if (this.collision.blocked(x, z, 0.7)) continue;
        this.add(kind, x, z);
        break;
      }
    }
  }

  /** Scatter around a point (e.g. tornado debris). */
  scatter(kind: PickupKind, n: number, cx: number, cz: number, radius: number): void {
    for (let i = 0; i < n && this.items.length < MAX_PICKUPS; i++) {
      for (let attempt = 0; attempt < 12; attempt++) {
        const a = this.rng.range(0, Math.PI * 2);
        const r = Math.sqrt(this.rng.next()) * radius;
        const x = cx + Math.cos(a) * r;
        const z = cz + Math.sin(a) * r;
        if (this.collision.blocked(x, z, 0.7)) continue;
        this.add(kind, x, z);
        break;
      }
    }
  }

  private add(kind: PickupKind, x: number, z: number): void {
    const object = new THREE.Group();
    if (kind === 'can') {
      const color = this.rng.pick(['#2f6fd6', '#c62828', '#d9dde0', '#e0b000']);
      const can = cyl(0.06, 0.06, 0.14, mat(color, { metal: 0.6, rough: 0.35 }), 0, 0.06, 0, 8);
      can.rotation.z = Math.PI / 2;
      can.scale.y = this.rng.range(0.55, 1);
      object.add(can);
    } else {
      const rust = mat(this.rng.pick(['#8f969c', '#7b4a2a', '#6d6d6d']), { metal: 0.6, rough: 0.6 });
      const variant = this.rng.int(0, 2);
      if (variant === 0) {
        const pipe = cyl(0.06, 0.06, 1.0, rust, 0, 0.06, 0, 8);
        pipe.rotation.z = Math.PI / 2;
        object.add(pipe);
      } else if (variant === 1) {
        object.add(cyl(0.3, 0.3, 0.05, M.chrome, 0, 0.03, 0, 14));
      } else {
        const rim = mesh(new THREE.TorusGeometry(0.3, 0.035, 5, 14), rust, 0, 0.04, 0);
        rim.rotation.x = Math.PI / 2;
        object.add(rim);
      }
    }
    object.position.set(x, 0, z);
    object.rotation.y = this.rng.range(0, Math.PI * 2);
    const glint = new THREE.Sprite(this.glintMat);
    glint.scale.setScalar(0.6);
    glint.position.set(x, 0.45, z);
    this.group.add(object, glint);
    this.items.push({ kind, object, glint, x, z, phase: this.rng.range(0, 6) });
  }

  /** Animates glints and returns whatever the player walked over. */
  update(time: number, px: number, pz: number, reach = 1.0): PickupKind[] {
    const got: PickupKind[] = [];
    for (let i = this.items.length - 1; i >= 0; i--) {
      const p = this.items[i] as Pickup;
      const pulse = 0.5 + 0.5 * Math.sin(time * 4 + p.phase);
      p.glint.scale.setScalar(0.35 + pulse * 0.45);
      p.glint.position.y = 0.35 + pulse * 0.15;
      const dx = p.x - px;
      const dz = p.z - pz;
      if (dx * dx + dz * dz < reach * reach) {
        p.object.removeFromParent();
        p.glint.removeFromParent();
        this.items.splice(i, 1);
        got.push(p.kind);
      }
    }
    return got;
  }
}
